import React, { Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { SiteSettingsProvider } from "@/contexts/SiteSettingsContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import { AuthProvider } from "./contexts/AuthContext";

// Critical homepage - keep static
import Index from "./pages/Index";

// Lazy load all other public pages
const Properties = React.lazy(() => import("./pages/Properties"));
const PropertyDetail = React.lazy(() => import("./pages/PropertyDetail"));
const About = React.lazy(() => import("./pages/About"));
const Contact = React.lazy(() => import("./pages/Contact"));
const Privacy = React.lazy(() => import("./pages/Privacy"));
const Terms = React.lazy(() => import("./pages/Terms"));
const NotFound = React.lazy(() => import("./pages/NotFound"));
const Projects = React.lazy(() => import("./pages/Projects"));
const Township = React.lazy(() => import("./pages/Township"));
const Spaces = React.lazy(() => import("./pages/Spaces"));
const Community = React.lazy(() => import("./pages/Community"));
const Portfolio = React.lazy(() => import("./pages/Portfolio"));
const Lifestyle = React.lazy(() => import("./pages/Lifestyle"));
const FloorPlans = React.lazy(() => import("./pages/FloorPlans"));
const Sustainability = React.lazy(() => import("./pages/Sustainability"));
const Blog = React.lazy(() => import("./pages/Blog"));
const BlogPost = React.lazy(() => import("./pages/BlogPost"));

// Lazy load all admin pages
const AdminLayout = React.lazy(() => import("./components/admin/AdminLayout"));
const AdminLogin = React.lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = React.lazy(() => import("./pages/admin/AdminDashboard"));
const AdminLeads = React.lazy(() => import("./pages/admin/AdminLeads"));
const AdminProperties = React.lazy(() => import("./pages/admin/AdminProperties"));
const AdminTestimonials = React.lazy(() => import("./pages/admin/AdminTestimonials"));
const AdminPartners = React.lazy(() => import("./pages/admin/AdminPartners"));
const AdminContent = React.lazy(() => import("./pages/admin/AdminContent"));
const AdminContentEditor = React.lazy(() => import("./pages/admin/AdminContentEditor"));
const AdminSEO = React.lazy(() => import("./pages/admin/AdminSEO"));
const AdminSettings = React.lazy(() => import("./pages/admin/AdminSettings"));
const AdminMedia = React.lazy(() => import("./pages/admin/AdminMedia"));
const AdminStats = React.lazy(() => import("./pages/admin/AdminStats"));
const AdminUsers = React.lazy(() => import("./pages/admin/AdminUsers"));
const AdminThemeEditor = React.lazy(() => import("./pages/admin/AdminThemeEditor"));
const VisualEditor = React.lazy(() => import("./pages/admin/VisualEditor"));
const AdminHeaderFooter = React.lazy(() => import("./pages/admin/AdminHeaderFooter"));
const AdminProjects = React.lazy(() => import("./pages/admin/AdminProjects"));
const AdminFloorPlans = React.lazy(() => import("./pages/admin/AdminFloorPlans"));
const AdminTownship = React.lazy(() => import("./pages/admin/AdminTownship"));
const AdminSustainability = React.lazy(() => import("./pages/admin/AdminSustainability"));
const AdminPortfolio = React.lazy(() => import("./pages/admin/AdminPortfolio"));
const AdminBlog = React.lazy(() => import("./pages/admin/AdminBlog"));
const AdminIntegrations = React.lazy(() => import("./pages/admin/AdminIntegrations"));
const AdminLifestyle = React.lazy(() => import("./pages/admin/AdminLifestyle"));
const AdminSpaces = React.lazy(() => import("./pages/admin/AdminSpaces"));
const AdminCommunity = React.lazy(() => import("./pages/admin/AdminCommunity"));

const queryClient = new QueryClient();

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => (
  <ErrorBoundary>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <ThemeProvider>
        <SiteSettingsProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Index />} />
            <Route path="/properties" element={<Properties />} />
            <Route path="/properties/:id" element={<PropertyDetail />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/township" element={<Township />} />
            <Route path="/spaces" element={<Spaces />} />
            <Route path="/community" element={<Community />} />
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/lifestyle" element={<Lifestyle />} />
            <Route path="/floorplans" element={<FloorPlans />} />
            <Route path="/sustainability" element={<Sustainability />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            
            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/visual-editor" element={<VisualEditor />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="leads" element={<AdminLeads />} />
              <Route path="properties" element={<AdminProperties />} />
              <Route path="testimonials" element={<AdminTestimonials />} />
              <Route path="partners" element={<AdminPartners />} />
              <Route path="content" element={<AdminContent />} />
              <Route path="content/:slug" element={<AdminContentEditor />} />
              <Route path="seo" element={<AdminSEO />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="media" element={<AdminMedia />} />
              <Route path="stats" element={<AdminStats />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="theme" element={<AdminThemeEditor />} />
              <Route path="header-footer" element={<AdminHeaderFooter />} />
              <Route path="projects" element={<AdminProjects />} />
              <Route path="floorplans" element={<AdminFloorPlans />} />
              <Route path="township" element={<AdminTownship />} />
              <Route path="sustainability" element={<AdminSustainability />} />
              <Route path="portfolio" element={<AdminPortfolio />} />
              <Route path="lifestyle" element={<AdminLifestyle />} />
              <Route path="spaces" element={<AdminSpaces />} />
              <Route path="community" element={<AdminCommunity />} />
              {/* About page managed via Visual Editor */}
              <Route path="blog" element={<AdminBlog />} />
              <Route path="integrations" element={<AdminIntegrations />} />
            </Route>
            
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
        </SiteSettingsProvider>
        </ThemeProvider>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
